import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  checkCleanupNestedValidation,
  cleanSecondaryText,
  DirectionEnum,
  Log,
  StationApi,
  type ICreateStation,
  type IDropdownItem,
  type IFormAttributes,
} from "@ryde/shared";

import type { IQuickStationSaveParams } from "./quick-add-station-form.models";

export interface IUseQuickAddStation {
  name: string;
  formAttributes?: IFormAttributes | null;
  closeQuickAdd: () => void;
  fetchItems: (forceRefetch?: boolean) => Promise<IDropdownItem[] | undefined>;
}

export const useQuickAddStation = ({
  name,
  formAttributes,
  closeQuickAdd,
  fetchItems,
}: IUseQuickAddStation) => {
  const { t } = useTranslation();

  const {
    setValue,
    formState: { errors },
  } = useFormContext();

  const onSave = useEvent(async (data: IQuickStationSaveParams) => {
    const payload: ICreateStation = {
      name: data.address,
      areaId: data.areaId,
      direction: DirectionEnum.Both,
      isDestination: false,
      metadata: {
        locationInfo: {
          latitude: data.coordinate.lat,
          longitude: data.coordinate.lng,
          fullAddress: cleanSecondaryText(data.address),
        },
        isActive: true,
        isOfficial: false,
      },
    };

    try {
      const station = await StationApi.createStation(payload);

      if (!station?.id) {
        Log.warnAndToast(
          t("errors.errorCreatingStation"),
          "Quick station creation returned no station",
        );
        return;
      }

      checkCleanupNestedValidation(name, errors);
      // Selected before the refetch: the booking option filters keep a station listed only once it is selected.
      setValue(name, station.id, {
        shouldDirty: true,
        shouldValidate: true,
      });

      // Not the shared station list (dropdownStationListAtom): the add-booking forms re-fill their station
      // fields from the passenger's stations whenever it changes, which would overwrite this selection.
      await fetchItems(true);
      // After the refetch, so sibling fields reloading from the dropdown cache already find the station.
      formAttributes?.onStationQuickAdded?.(station.id);

      closeQuickAdd();
    } catch (error) {
      Log.warnAndToast(t("errors.errorCreatingStation"), error);
    }
  });

  return { onSave };
};
